import {
  deleteV1CompaniesCompanyIdFuelPricesId,
  getV1CompaniesCompanyIdFuelPriceIndicesFuelPriceIndexIdFuelPrices,
  patchV1CompaniesCompanyIdFuelPricesId,
  postV1CompaniesCompanyIdFuelPriceIndicesFuelPriceIndexIdFuelPrices,
} from '@treadinc/horizon-api-spec';
import { useState } from 'react';

import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { FuelPrice } from './models';

interface GetFuelPricesParams {
  fuelPriceIndexId: string;
  limit?: number;
  cursor?: string;
  link?: PaginationLink;
  signal?: AbortSignal;
}

interface CreateFuelPriceParams {
  fuelPriceIndexId: string;
  date: string;
  price: number;
}

interface UpdateFuelPriceParams {
  id: string;
  date: string;
  price: number;
}

export function useFuelPrices() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const getFuelPrices = async ({
    fuelPriceIndexId,
    limit = 20,
    cursor,
    link,
    signal,
  }: GetFuelPricesParams) => {
    const query = {
      'page[limit]': limit,
      ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
    };

    try {
      const response =
        await getV1CompaniesCompanyIdFuelPriceIndicesFuelPriceIndexIdFuelPrices({
          path: {
            'company-id': companyId,
            'fuel-price-index-id': fuelPriceIndexId,
          },
          query,
          signal,
        });
      const parsedData = response.data.data.map(FuelPrice.parse);
      const pagination = extractPagination(response);
      return { data: parsedData, pagination };
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      connection.handleRequestError(error);
      throw error;
    }
  };

  const createFuelPrice = async ({
    fuelPriceIndexId,
    date,
    price,
  }: CreateFuelPriceParams) => {
    try {
      setIsCreating(true);
      const response =
        await postV1CompaniesCompanyIdFuelPriceIndicesFuelPriceIndexIdFuelPrices({
          path: {
            'company-id': companyId,
            'fuel-price-index-id': fuelPriceIndexId,
          },
          body: { date, price },
        });
      return FuelPrice.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  const updateFuelPrice = async ({ id, date, price }: UpdateFuelPriceParams) => {
    try {
      setIsUpdating(true);
      const response = await patchV1CompaniesCompanyIdFuelPricesId({
        path: { 'company-id': companyId, id },
        body: { date, price },
      });
      return FuelPrice.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteFuelPrice = async (id: string) => {
    try {
      setIsDeleting(true);
      await deleteV1CompaniesCompanyIdFuelPricesId({
        path: { 'company-id': companyId, id },
      });
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    getFuelPrices,
    createFuelPrice,
    updateFuelPrice,
    deleteFuelPrice,
    isCreating,
    isUpdating,
    isDeleting,
  };
}
